import { DateTime } from 'luxon';

export type DatePresetBundle = 'past' | 'future';

export type PastPresetId =
  | 'today'
  | 'sevenDays'
  | 'fourWeeks'
  | 'sixMonths'
  | 'twelveMonths'
  | 'monthToDate'
  | 'quarterToDate'
  | 'yearToDate'
  | 'allTime';

export type FuturePresetId =
  | 'today'
  | 'thisWeek'
  | 'thisWeekend'
  | 'sevenDays'
  | 'thirtyDays'
  | 'restOfMonth'
  | 'threeMonths'
  | 'twelveMonths'
  | 'allUpcoming';

export type DatePresetId = PastPresetId | FuturePresetId;

export type PastPresetRange = { id: PastPresetId; value: [Date, Date] };
export type FuturePresetRange = { id: FuturePresetId; value: [Date, Date] };
export type DatePresetRange = PastPresetRange | FuturePresetRange;

/**
 * A caller-supplied custom preset. Mirrors the subset of vue-datepicker's
 * `PresetDate` we expose: a label plus a 2-element range (Dates or ISO strings).
 */
export type DatePresetOption = { label: string; range: Date[] | string[] };

const ALL_TIME_PAST = DateTime.fromObject({ year: 2000, month: 1, day: 1 });
const UPCOMING_END = DateTime.fromObject({ year: 2100, month: 12, day: 31 });

type RangeEntry<Id extends DatePresetId> = [Id, [DateTime, DateTime]];

const mapRanges = <Id extends DatePresetId>(entries: RangeEntry<Id>[]): { id: Id; value: [Date, Date] }[] =>
  entries.map(([id, [start, end]]) => ({ id, value: [start.toJSDate(), end.toJSDate()] }));

/**
 * Quick-range presets relative to `now`: `past` ends today, `future` starts today.
 * Weeks are ISO (Mon-Sun), so the weekend falls inside the week it belongs to.
 */
export function buildPresetRanges(bundle: 'past', now?: DateTime): PastPresetRange[];
export function buildPresetRanges(bundle: 'future', now?: DateTime): FuturePresetRange[];
export function buildPresetRanges(bundle: DatePresetBundle, now?: DateTime): DatePresetRange[];
export function buildPresetRanges(bundle: DatePresetBundle, now: DateTime = DateTime.now()): DatePresetRange[] {
  if (bundle === 'future') {
    const start = now.startOf('day');
    // ISO `weekday`, so 5/6/7 is Fri/Sat/Sun: the weekend has begun, don't reopen it.
    const weekendStart = now.weekday >= 5 ? start : start.plus({ days: 5 - now.weekday });
    const weekendEnd = start.plus({ days: 7 - now.weekday }).endOf('day');

    return mapRanges<FuturePresetId>([
      ['today', [start, now.endOf('day')]],
      ['thisWeek', [start, now.endOf('week')]],
      ['thisWeekend', [weekendStart, weekendEnd]],
      ['sevenDays', [start, now.plus({ days: 7 }).endOf('day')]],
      ['thirtyDays', [start, now.plus({ days: 30 }).endOf('day')]],
      ['restOfMonth', [start, now.endOf('month')]],
      ['threeMonths', [start, now.plus({ months: 3 }).endOf('day')]],
      ['twelveMonths', [start, now.plus({ months: 12 }).endOf('day')]],
      ['allUpcoming', [start, UPCOMING_END]],
    ]);
  }

  const today = now.endOf('day');

  return mapRanges<PastPresetId>([
    ['today', [now.startOf('day'), today]],
    ['sevenDays', [now.minus({ days: 7 }).startOf('day'), today]],
    ['fourWeeks', [now.minus({ weeks: 4 }).startOf('day'), today]],
    ['sixMonths', [now.minus({ months: 6 }).startOf('day'), today]],
    ['twelveMonths', [now.minus({ months: 12 }).startOf('day'), today]],
    ['monthToDate', [now.startOf('month'), today]],
    ['quarterToDate', [now.startOf('quarter'), today]],
    ['yearToDate', [now.startOf('year'), today]],
    ['allTime', [ALL_TIME_PAST, today]],
  ]);
}
